import React, { useState } from "react";
import {
    Box,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Radio,
    RadioGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    tableCellClasses,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import SelectField from "components/Select";
import { PrimaryButton } from "components/Buttons";
import Colors from "assets/Style/Colors";
import { FontFamily } from "assets";
import { ErrorToaster, SuccessToaster } from "components/Toaster";
import InputField from "components/Input";
import DatePicker from "components/DatePicker";
import {
    getFileSize,
} from "utils";
import { Add } from "@mui/icons-material";
import styled from "@emotion/styled";
import UploadFile from "components/UploadFile";
import { v4 as uuidv4 } from "uuid";
import { CleanTypes } from "utils";
import instance from "config/axios";
import routes from "services/System/routes";
import { useAuth } from "context/UseContext";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { emailRegex } from 'utils/index';
import InputPhone from "components/InputPhone";
import BuyerServices from "services/Buyer";
import { FiDownload } from "react-icons/fi";
import DeleteIcon from '@mui/icons-material/Delete';

// *For Table Style
const Row = styled(TableRow)(({ theme }) => ({
    border: 0,
}));

const Cell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        fontSize: 14,
        fontFamily: FontFamily.NunitoRegular,
        border: 0,
        textAlign: "left",
        whiteSpace: "nowrap",
        backgroundColor: Colors.primary,
        color: Colors.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        fontFamily: FontFamily.NunitoRegular,
        textAlign: "left",
        textWrap: "nowrap",

        ".MuiBox-root": {
            display: "flex",
            gap: "6px",
            alignItems: "center",
            justifyContent: "center",
            ".MuiBox-root": {
                cursor: "pointer",
            },
        },
        svg: {
            width: "auto",
            height: "24px",
        },
        ".MuiTypography-root": {
            textTransform: "capitalize",
            fontFamily: FontFamily.NunitoRegular,
            textWrap: "nowrap",
        },
        ".css-1ru1j9e-MuiTableCell-root ": {
            color: "#ffffff",
            textAlign: "left",

        },
        ".MuiButtonBase-root": {
            padding: "8px",
            width: "28px",
            height: "28px",
        },
    },
}));

const allowFilesType = [
    "image/png",
    "image/jpg",
    "image/jpeg",
    "application/pdf",
];

function CreateCompany() {
    const navigate = useNavigate();
    const { user, userLogout } = useAuth();

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        getValues,
        resetField,
        control,
    } = useForm();
    const [loading, setLoading] = useState(false);

    const [companyStatus, setCompanyStatus] = useState("Not Suspend");
    const [showMasterPassword, setShowMasterPassword] = useState(false);

    const [companyExpiryDate, setCompanyExpiryDate] = useState(new Date());
    const [documentExpiryDate, setDocumentExpiryDate] = useState(new Date());

    const [progress, setProgress] = useState(0);
    const [uploadedSize, setUploadedSize] = useState(0);
    const [documentDetail, setDocumentDetail] = useState([]);
    const [documentLink, setDocumentLink] = useState("");
    const [selectedIdType, setSelectedIdType] = useState(null);
    const [documentList, setDocumentList] = useState([]);

    const downloadAllAttachments = async (documentLinks) => {

        if (!documentLinks || documentLinks.trim() === "") {
            ErrorToaster("No attachments available.");
            return;
        }

        const filePath = documentLinks.trim();
        const fileUrl = `${process.env.REACT_APP_IMAGE_BASE_URL}${filePath}`;
        const fileExtension = filePath.split('.').pop();

        try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute("download", `attachment.${fileExtension}`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(blobUrl); // Cleanup
            SuccessToaster("Downloading attachment...");
        } catch (error) {
            console.error("Download failed", error);
            ErrorToaster("Failed to download attachment.");
        }
    };


    const IDTYPES = [
        { id: "passport", name: "Passport" },
        { id: "e-id", name: "E-ID" },
        { id: "tax-document", name: "Tax Document" },
        { id: "trade-license", name: "Trade License" },
    ];

    const handleCompanyExpiryDate = (newDate) => {
        try {
            if (newDate == "Invalid Date") {
                setCompanyExpiryDate("invalid");
                return;
            }
            setCompanyExpiryDate(new Date(newDate));
        } catch (error) {
            ErrorToaster(error);
        }
    };


    const handleDocumentExpiryDate = (newDate) => {
        try {
            if (newDate == "Invalid Date") {
                setDocumentExpiryDate("invalid");
                return;
            }
            setDocumentExpiryDate(new Date(newDate));
        } catch (error) {
            ErrorToaster(error);
        }
    };

    const handleUploadDocument = async (e) => {
        try {
            e.preventDefault();
            const file = e.target.files[0];
            let arr = [
                {
                    id: uuidv4(),
                    name: file?.name,
                    file: "",
                    type: file?.type.split("/")[1],
                    size: getFileSize(file.size),
                    isUpload: false,
                },
            ];
            if (allowFilesType.includes(file.type)) {
                setDocumentDetail(arr);
                handleUpload(file, arr);
            } else {
                ErrorToaster(`Only ${CleanTypes(allowFilesType)} formats is supported`);
            }
        } catch (error) {
            ErrorToaster(error);
        }
    };

    const handleUpload = async (file, docs) => {
        setProgress(0);
        try {
            const formData = new FormData();
            formData.append("document", file);
            const { data } = await instance.post(routes.uploadDocuments, formData, {
                onUploadProgress: (progressEvent) => {
                    const uploadedBytes = progressEvent.loaded;
                    const percentCompleted = Math.round(
                        (uploadedBytes * 100) / progressEvent.total
                    );

                    setProgress(percentCompleted);
                    setUploadedSize(getFileSize(uploadedBytes));
                },
            });
            if (data) {
                docs[0].isUpload = true;
                docs[0].file = data?.data?.nations;
                setDocumentDetail(docs);
                setDocumentLink(data?.data?.nations);
                SuccessToaster("Document Uploaded")
            }
        } catch (error) {
            ErrorToaster(error);
        }
    };

    const handleDeleteDocument = (id) => {
        setDocumentList((prev) => prev.filter((d) => d.id !== id));
    };

    const removeDoc = () => {
        try {
            setDocumentDetail([]);
            setDocumentLink("");
            setValue("scanned", "");
        } catch (error) {
            ErrorToaster(error);
        }
    };

    const handleAddDocument = () => {

        if (!documentLink) {
            ErrorToaster("Please upload a document before adding.");
            return;
        }

        if (!selectedIdType) {
            ErrorToaster("Please select document type.");
            return;
        }

        setDocumentList([
            ...documentList,
            {
                id: uuidv4(),
                type: selectedIdType?.name,
                expiryDate: documentExpiryDate,
                documentLink,
            },
        ]);
        SuccessToaster("Document Added")
        setSelectedIdType(null);
        setDocumentExpiryDate(new Date());
        setDocumentLink("");
    };


    const createOrganization = async (formData) => {
        setLoading(true);
        try {
            const documents = documentList.map((doc) => {
                return {
                    type: doc.type,
                    expiry_date: doc.expiryDate,
                    document_link: doc.documentLink,
                };
            });

            const payload = {
                full_e_name: formData.fullName,
                short_name: formData.shortName,
                master_id: formData.masterId,
                master_email: formData.masterEmail,
                master_password: formData.MasterPassword,
                owner: formData.owner,
                expiry_date: companyExpiryDate,
                phone: formData.mobileNumber,
                remarks: formData.remark,
                status: companyStatus,
                documents: documents,
            };


            const { message } = await BuyerServices.createOrganization(payload);

            SuccessToaster(message);
            navigate('/company-list')
        } catch (error) {
            ErrorToaster(error.message);
        } finally {
            setLoading(false);
        }
    };


    return (
        <Box
            sx={{
                m: 4,
                p: 5,
                bgcolor: Colors.white,
                borderRadius: 3,
                boxShadow: "0px 8px 18px 0px #9B9B9B1A",
            }}
        >

            <Box component="form" onSubmit={handleSubmit(createOrganization)}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12}>
                        <Typography
                            variant="h5"
                            sx={{
                                color: Colors.charcoalGrey,
                                fontFamily: FontFamily.NunitoRegular,
                                mb: 4,
                            }}
                        >
                            Create Company
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <InputField
                            size={"small"}
                            label={"Full Entity Name*"}
                            placeholder={"Full Entity Name"}
                            error={errors?.fullName?.message}
                            register={register("fullName", {
                                required: 'Please Enter Full Name.',
                            })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputField
                            size={"small"}
                            label={"Short Name*"}
                            placeholder={"Short Name"}
                            error={errors?.shortName?.message}
                            register={register("shortName", {
                                required: 'Please Enter Short Name.',
                            })}
                        />
                    </Grid>
                    {/* <Grid item xs={12} sm={4}>
                        <InputField
                            disabled={true}
                            size={"small"}
                            label={"Master ID"}
                            placeholder={"Master ID"}
                            register={register("masterId", {
                                // required: 'Please Enter Master ID.',
                            })}
                        />
                    </Grid> */}

                    <Grid item xs={12} sm={4}>
                        <InputField
                            // disabled={true}

                            size={"small"}
                            label={"Master Email*"}
                            type="email"
                            placeholder={"Master Email"}
                            error={errors?.masterEmail?.message}
                            register={register("masterEmail", {
                                required: "Please enter your email.",
                                pattern: {
                                    value: emailRegex,
                                    message: "Please enter a valid email.",
                                },
                            })}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <InputField
                            size={"small"}
                            label={"Master Email Password*"}
                            placeholder={"Master Email Password"}
                            type={showMasterPassword ? "text" : "password"}
                            // disabled={true}

                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            edge="end"
                                            onClick={() => setShowMasterPassword(!showMasterPassword)}
                                        >
                                            {showMasterPassword ? (
                                                <Visibility color="primary" />
                                            ) : (
                                                <VisibilityOff color="primary" />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            error={errors?.MasterPassword?.message}
                            register={register("MasterPassword", {
                                required: "Please Enter Master Password.",
                            })}
                        />
                    </Grid>


                    <Grid item xs={12} sm={4}>
                        <InputField
                            size={"small"}
                            label={"Owner*"}
                            placeholder={"Owner"}
                            error={errors?.owner?.message}
                            register={register("owner", {
                                required: 'Please Enter Owner.',
                            })}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <DatePicker
                            size={"small"}
                            label={"Company Expiry Date"}
                            value={companyExpiryDate}

                            onChange={(date) => handleCompanyExpiryDate(date)}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <InputPhone
                            label={"Mobile Number For OTP*"}
                            name={"mobileNumber"}
                            control={control}
                            borderRadius="12px"
                            size='small'
                            height='45px'
                            rules={{
                                required:
                                    "Please enter your international mobile number.",
                            }}
                            error={errors?.mobileNumber?.message}
                            register={register("mobileNumber", {
                                required: 'Please Enter Moile Number.',
                            })}

                        />
                    </Grid>


                    <Grid item xs={12} sm={4}>
                        <InputField
                            size={"small"}
                            label={"Remarks"}
                            placeholder={"Remarks"}
                            register={register("remark")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: Colors.charcoalGrey,
                                fontFamily: FontFamily.NunitoRegular,
                            }}
                        >
                            Status
                        </Typography>
                        <FormControl>
                            <RadioGroup
                                row
                                value={companyStatus}
                                onChange={(e) => setCompanyStatus(e.target.value)}
                            >

                                <span style={{ display: "inline-block" }}>
                                    <FormControlLabel
                                        value="Suspend"
                                        control={<Radio />}
                                        label="Suspend"

                                    />
                                </span>
                                <FormControlLabel
                                    value="Not Suspend"
                                    control={<Radio />}
                                    label="Not Suspend"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    <Grid container spacing={2} padding={2}>
                        <Grid item xs={12} sm={3.5}>
                            <SelectField
                                label="Document Type"
                                options={IDTYPES}
                                selected={selectedIdType}
                                onSelect={(value) => setSelectedIdType(value)}
                            />
                        </Grid>

                        <Grid item xs={12} sm={3.5}>
                            <DatePicker
                                label="Document Expiry Date"
                                value={documentExpiryDate}
                                onChange={(date) => handleDocumentExpiryDate(date)}
                            />
                        </Grid>

                        <Grid item xs={12} sm={3.5} mt={3.7}>
                            <UploadFile
                                custom={true}
                                style={{
                                    backgroundColor: "#e7efeb",
                                    width: "100%",
                                    height: "40px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "20px",
                                    border: "1px dashed #aeb1b0",
                                    borderRadius: "10px",
                                }}
                                register={register("scanned", {
                                    onChange: (e) => handleUploadDocument(e),
                                })}
                            />
                        </Grid>

                        {/* <Grid item xs={12} sm={4}>
                        {documentDetail.length > 0 && (
                            <Typography
                                variant="body1"
                                sx={{ color: Colors.charcoalGrey, mb: 1 }}
                            >
                                Uploaded Files
                            </Typography>
                        )}
                        <Box sx={{ maxHeight: 300, overflow: "auto", pr: 1 }}>
                            {documentDetail?.map((item, index) => (
                                <Uploading
                                    key={index}
                                    data={item}
                                    uploadedSize={uploadedSize}
                                    progress={progress}
                                    removeDoc={() => removeDoc()}
                                />
                            ))}
                        </Box> 
                    </Grid> */}

                        <Grid item xs={12} sm={1} sx={{ textAlign: "right" }} mt={4}>
                            <IconButton
                                size="small"
                                onClick={handleAddDocument}
                                sx={{
                                    bgcolor: Colors.primary,
                                    color: Colors.white,
                                    "&:hover": {
                                        bgcolor: Colors.primary,
                                        color: Colors.white,
                                    },
                                }}
                            >
                                <Add />
                            </IconButton>
                        </Grid>


                        {
                            documentList.length > 0 ? (
                                <>
                                    <Grid item xs={12} sm={12}>
                                        <TableContainer component={Paper}>
                                            <Table sx={{ minWidth: 650 }} aria-label="Documents Table">
                                                <TableHead sx={{ bgcolor: Colors.primary, color: Colors.white }}>
                                                    <TableRow>
                                                        <Cell>Document Type</Cell>
                                                        <Cell>Expiry Date</Cell>
                                                        <Cell>Attachment</Cell>
                                                        <Cell>Action</Cell>

                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {documentList.map((doc) => (
                                                        <TableRow key={doc?.id}>
                                                            <TableCell>{doc?.type}</TableCell>
                                                            <TableCell>{new Date(doc?.expiryDate).toLocaleDateString()}</TableCell>
                                                            <TableCell>

                                                                {doc?.documentLink && doc?.documentLink ? (
                                                                    <div style={{ cursor: "pointer" }} onClick={() => downloadAllAttachments(doc?.documentLink)}>
                                                                        <FiDownload color={Colors.primary} size={25} />
                                                                    </div>
                                                                ) : (
                                                                    <FiDownload color="gray" size={25} />
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <IconButton
                                                                    aria-label="delete document"
                                                                    onClick={() => handleDeleteDocument(doc.id)}
                                                                    sx={{ color: Colors.danger }}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>

                                </>
                            ) : (
                                <>
                                </>
                            )
                        }
                    </Grid>

                    <Grid item xs={12} sm={12} sx={{ mt: 2, textAlign: "right" }}>
                        <PrimaryButton title="Submit" type="submit" loading={loading} />
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}

export default CreateCompany;
